import type { ComponentProps, ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface PanelProps extends Omit<ComponentProps<'section'>, 'title'> {
  /** Header row: title (with optional inline meta) on the left, actions on the right. */
  title?: ReactNode;
  /** Short context printed beside the title, e.g. a count or a period. */
  meta?: ReactNode;
  actions?: ReactNode;
  /** No inner padding: for edge-to-edge lists and tables (the header keeps its own padding). */
  flush?: boolean;
}

/**
 * The flat surface everything readable sits on (docs/DESIGN.md §1). Hairline edge, no shadow, no
 * blur. Use a handful per screen; rows inside are divided by hairlines, never boxed again.
 */
export function Panel({ title, meta, actions, flush, className, children, ...props }: PanelProps) {
  const hasHeader = title !== undefined || actions !== undefined;
  return (
    <section className={cn('panel min-w-0', !flush && 'p-4', className)} {...props}>
      {hasHeader && (
        <header
          className={cn(
            'flex min-h-8 items-center justify-between gap-3',
            flush ? 'px-4 pt-3 pb-2.5' : 'mb-3 -mt-0.5',
          )}
        >
          <div className="flex min-w-0 items-baseline gap-2">
            {title !== undefined && <h2 className="type-section truncate text-foreground">{title}</h2>}
            {meta !== undefined && (
              <span className="truncate text-[0.75rem] text-muted-foreground num">{meta}</span>
            )}
          </div>
          {actions !== undefined && <div className="flex shrink-0 items-center gap-1.5">{actions}</div>}
        </header>
      )}
      {children}
    </section>
  );
}
